import React, { useState, useEffect } from "react";
import { Box, Divider } from "@mui/material";
import {
  analyzeBill,
  mapBillDataToForm,
} from "../../../../../../services/billAnalysisService";
import HeroSection from "./components/HeroSection";
import UploadSection from "./components/UploadSection";
import FeaturesSection from "./components/FeaturesSection";
import KPICards from "./components/KPICards";
import BenefitsSection from "./components/BenefitsSection";
import QuoteDialog from "./components/QuoteDialog";
import BillResultsTable from "./components/BillResultsTable";
import BillDetailsDialog from "./components/BillDetailsDialog";
import TariffeFornitori from "./components/TariffeFornitori";
import { useFormRCAutoDetails } from "../../../rcautoContext";
import PersonaCard from "../PersonaCard";
import { generateUtilitiesMock } from "../personaMockHelpers";

function Utilities() {
  const { formState } = useFormRCAutoDetails();
  const [showForm, setShowForm] = useState(false); // Toggle tra card e form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  const [methodChosen, setMethodChosen] = useState(null); // 'ai' or 'manual'
  const [analyzedBills, setAnalyzedBills] = useState([]); // Array delle bollette analizzate
  const [selectedBill, setSelectedBill] = useState(null); // Bolletta selezionata per i dettagli
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false); // Dialog dettagli bolletta
  const [errors, setErrors] = useState({
    servizio: false,
    comune: false,
    tipologia: false,
    mercato: false,
    fornitore: false,
    cambiatoFornitori: false,
    personeInCasa: false,
    nome: false,
    cognome: false,
    email: false,
    telefono: false,
    codiceFiscale: false,
    privacyAccettata: false,
  });

  const [formData, setFormData] = useState({
    servizio: "luce", // luce, gas, luce-gas
    comune: "",
    tipologia: "privato", // privato, business
    mercato: "libero", // tutelato, libero
    fornitore: "",
    cambiatoFornitori: "", // si, no
    personeInCasa: "", // 1, 2, 3, 4, 5+
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    codiceFiscale: "",
    indirizzo: "",
    cap: "",
    privacyAccettata: false,
    consensoPromozionali: false,
    consensoPartner: false,
  });

  // Precompila i campi con i dati del cliente quando il dialog si apre
  useEffect(() => {
    if (dialogOpen && formState?.cliente && methodChosen !== "ai") {
      const cliente = formState.cliente;
      
      setFormData((prev) => {
        const updates = { ...prev };
        
        if (cliente.nome) updates.nome = cliente.nome;
        if (cliente.cognome) updates.cognome = cliente.cognome;
        if (cliente.email) updates.email = cliente.email;
        if (cliente.telefono) updates.telefono = cliente.telefono;
        if (cliente.cf) updates.codiceFiscale = cliente.cf;
        
        // Mappa l'indirizzo del cliente con tipologia via
        if (cliente.slResIndirizzo) {
          let indirizzo = "";
          
          // Aggiungi la tipologia della via se presente (Via, Viale, ecc.)
          if (cliente.slResIndirizzoVia) {
            if (typeof cliente.slResIndirizzoVia === "object" && cliente.slResIndirizzoVia.label) {
              indirizzo = cliente.slResIndirizzoVia.label + " ";
            } else {
              indirizzo = cliente.slResIndirizzoVia + " ";
            }
          }
          
          indirizzo += cliente.slResIndirizzo;
          
          if (cliente.slResCivico) {
            indirizzo += " " + cliente.slResCivico;
          }
          
          updates.indirizzo = indirizzo;
        }
        
        // Mappa il CAP del cliente
        if (cliente.slResCap) {
          // Se slResCap è un oggetto, usa il value, altrimenti usa direttamente
          if (typeof cliente.slResCap === "object" && cliente.slResCap.value) {
            updates.cap = cliente.slResCap.value;
          } else {
            updates.cap = cliente.slResCap;
          }
        }
        
        // Mappa il comune del cliente - usa il nome del comune, non il codice o il CAP
        // Cerca prima in slResComune (se esiste), poi prova a estrarre da slResComuneCodice
        if (cliente.slResComune) {
          updates.comune = cliente.slResComune;
        } else if (cliente.slResComuneCodice) {
          // Se l'oggetto ha un label (nome del comune), usalo
          if (typeof cliente.slResComuneCodice === "object" && cliente.slResComuneCodice.label) {
            updates.comune = cliente.slResComuneCodice.label;
          }
          // Altrimenti lascia vuoto piuttosto che mettere il codice
        }
        
        return updates;
      });
    }
  }, [dialogOpen, formState?.cliente, methodChosen]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setShowSuccess(false);
    setActiveStep(0);

    // Solo resetta i dati se non ci sono già dati precompilati dall'AI
    if (methodChosen !== "ai") {
      setFormData({
        servizio: "luce",
        comune: "",
        tipologia: "privato",
        mercato: "libero",
        fornitore: "",
        cambiatoFornitori: "",
        personeInCasa: "",
        nome: "",
        cognome: "",
        email: "",
        telefono: "",
        codiceFiscale: "",
        indirizzo: "",
        cap: "",
        privacyAccettata: false,
        consensoPromozionali: false,
        consensoPartner: false,
      });
      setErrors({
        servizio: false,
        comune: false,
        tipologia: false,
        mercato: false,
        fornitore: false,
        cambiatoFornitori: false,
        personeInCasa: false,
        nome: false,
        cognome: false,
        email: false,
        telefono: false,
        codiceFiscale: false,
        privacyAccettata: false,
      });
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setShowSuccess(false);
    setActiveStep(0);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const validateStep = (step) => {
    const newErrors = { ...errors };
    let isValid = true;

    switch (step) {
      case 0:
        if (!formData.servizio) {
          newErrors.servizio = true;
          isValid = false;
        }
        if (!formData.comune || formData.comune.trim() === "") {
          newErrors.comune = true;
          isValid = false;
        }
        if (!formData.tipologia) {
          newErrors.tipologia = true;
          isValid = false;
        }
        if (!formData.mercato) {
          newErrors.mercato = true;
          isValid = false;
        }
        if (!formData.fornitore) {
          newErrors.fornitore = true;
          isValid = false;
        }
        break;
      case 1:
        if (!formData.cambiatoFornitori) {
          newErrors.cambiatoFornitori = true;
          isValid = false;
        }
        if (!formData.personeInCasa) {
          newErrors.personeInCasa = true;
          isValid = false;
        }
        break;
      case 2:
        if (!formData.nome || formData.nome.trim() === "") {
          newErrors.nome = true;
          isValid = false;
        }
        if (!formData.cognome || formData.cognome.trim() === "") {
          newErrors.cognome = true;
          isValid = false;
        }
        if (!formData.telefono || formData.telefono.trim() === "") {
          newErrors.telefono = true;
          isValid = false;
        }
        if (
          !formData.email ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ) {
          newErrors.email = true;
          isValid = false;
        }
        break;
      case 3:
        if (!formData.privacyAccettata) {
          newErrors.privacyAccettata = true;
          isValid = false;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === 3) {
        // Submit form e mostra pagina di successo
        console.log("Form submitted:", formData);
        setShowSuccess(true);
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setFormData({
      servizio: "luce",
      comune: "",
      tipologia: "privato",
      mercato: "libero",
      fornitore: "",
      cambiatoFornitori: "",
      personeInCasa: "",
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      codiceFiscale: "",
      indirizzo: "",
      cap: "",
      privacyAccettata: false,
      consensoPromozionali: false,
      consensoPartner: false,
    });
    setErrors({
      servizio: false,
      comune: false,
      tipologia: false,
      mercato: false,
      fornitore: false,
      cambiatoFornitori: false,
      personeInCasa: false,
      nome: false,
      cognome: false,
      email: false,
      telefono: false,
      codiceFiscale: false,
      privacyAccettata: false,
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setUploadedFile(file);
      analyzeFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setUploadedFile(file);
      analyzeFile(file);
    }
  };

  const analyzeFile = async (file) => {
    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisSuccess(false);

    try {
      const result = await analyzeBill(file);

      console.log("Risultato analisi:", result);

      if (result.success && result.data) {
        // Map bill data to form
        const mappedData = mapBillDataToForm(result.data);

        console.log("Dati mappati per il form:", mappedData);

        // Update form data with extracted information
        setFormData((prev) => {
          const newData = {
            ...prev,
            ...mappedData,
          };
          console.log("Form data aggiornato:", newData);
          return newData;
        });

        // Aggiungi la bolletta analizzata all'array
        const newBill = {
          id: Date.now(), // ID univoco
          fileName: file.name,
          uploadDate: new Date().toLocaleString("it-IT"),
          ...result.data,
          ...mappedData,
        };

        setAnalyzedBills((prev) => [...prev, newBill]);
        setAnalysisSuccess(true);
        setMethodChosen("ai");
      } else {
        setAnalysisError(result.error || "Impossibile analizzare la bolletta");
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      setAnalysisError("Errore durante l'analisi della bolletta");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setAnalysisError(null);
    setAnalysisSuccess(false);
    setMethodChosen(null);
  };

  const handleManualEntry = () => {
    setMethodChosen("manual");
    setUploadedFile(null);
    setAnalysisError(null);
    setAnalysisSuccess(false);

    // Reset form data per inserimento manuale
    setFormData({
      servizio: "luce",
      comune: "",
      tipologia: "privato",
      mercato: "libero",
      fornitore: "",
      cambiatoFornitori: "",
      personeInCasa: "",
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      codiceFiscale: "",
      indirizzo: "",
      cap: "",
      privacyAccettata: false,
      consensoPromozionali: false,
      consensoPartner: false,
    });

    // Apri il dialog per l'inserimento manuale
    if (!dialogOpen) {
      setDialogOpen(true);
      setShowSuccess(false);
      setActiveStep(0);
    }
  };

  const validateFile = (file) => {
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      alert("Formato file non valido. Carica PDF, JPG o PNG.");
      return false;
    }

    if (file.size > maxSize) {
      alert("File troppo grande. Massimo 10MB.");
      return false;
    }

    return true;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleViewDetails = (bill) => {
    setSelectedBill(bill);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedBill(null);
  };

  return (
    <Box>
      {/* Mostra la card se showForm è false */}
      {!showForm && (
        <PersonaCard
          preventivo={generateUtilitiesMock(formState?.cliente)}
          onModifica={() => setShowForm(true)}
        />
      )}

      {/* Mostra il form completo se showForm è true */}
      {showForm && (
        <>
          <HeroSection />

          <UploadSection
            uploadedFile={uploadedFile}
            isAnalyzing={isAnalyzing}
            analysisSuccess={analysisSuccess}
            analysisError={analysisError}
            onFileDrop={handleFileDrop}
            onFileSelect={handleFileSelect}
            onDragOver={handleDragOver}
            onRemoveFile={handleRemoveFile}
            onManualEntry={handleManualEntry}
            onOpenDialog={handleOpenDialog}
          />

          {/* Mostra la tabella con le bollette analizzate */}
          {analyzedBills.length > 0 && (
            <BillResultsTable bills={analyzedBills} onViewDetails={handleViewDetails} />
          )}

          {/* Modulo Gestione Tariffe Fornitori */}
          <Box sx={{ my: 6 }}>
            <Divider sx={{ mb: 4 }} />
            <TariffeFornitori />
          </Box>

          <FeaturesSection />
          <KPICards />
          <BenefitsSection />

          <QuoteDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            showSuccess={showSuccess}
            activeStep={activeStep}
            formData={formData}
            errors={errors}
            uploadedFile={uploadedFile}
            isAnalyzing={isAnalyzing}
            analysisSuccess={analysisSuccess}
            analysisError={analysisError}
            methodChosen={methodChosen}
            onFileDrop={handleFileDrop}
            onFileSelect={handleFileSelect}
            onDragOver={handleDragOver}
            onRemoveFile={handleRemoveFile}
            onManualEntry={handleManualEntry}
            handleChange={handleChange}
            handleNext={handleNext}
            handleBack={handleBack}
            onReset={handleReset}
          />

          {/* Dialog per visualizzare i dettagli della bolletta */}
          <BillDetailsDialog
            open={detailsDialogOpen}
            onClose={handleCloseDetailsDialog}
            bill={selectedBill}
          />
        </>
      )}
    </Box>
  );
}

export default Utilities;
